import { useState } from "react";

interface Props {
  accept: string;
  multiple?: boolean;
  label: string;
  onFiles: (files: File[]) => void;
  // A run cannot start without this file, so the box must read as the thing to do next
  // rather than blend in with the optional prior-period boxes around it.
  required?: boolean;
  status?: "done" | "error";
}

// Mirrors the browser dialog's own `accept` filter, which a drop bypasses.
function accepted(file: File, accept: string): boolean {
  const name = file.name.toLowerCase();
  return accept.split(",").some((ext) => name.endsWith(ext.trim().toLowerCase()));
}

// Styled file dropzone replacing raw browser file chrome. Click to browse or drop files
// onto it; the styling comes from .filedrop in theme.css (palette-matched).
export default function FilePicker({ accept, multiple, label, onFiles, required, status }: Props) {
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);

  const cls = ["filedrop",
    required && "filedrop--required",
    status && `filedrop--${status}`,
    over && "filedrop--over"].filter(Boolean).join(" ");

  return (
    <>
      {/* No onClick: a label already opens the input it wraps, and a second ref.click()
          opened the file dialog twice. */}
      <label className={cls}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={(e) => {
          // dragleave also fires when crossing into the icon/label children.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const dropped = Array.from(e.dataTransfer.files);
          const ok = dropped.filter((f) => accepted(f, accept));
          setRejected(dropped.filter((f) => !accepted(f, accept)).map((f) => f.name));
          const files = multiple ? ok : ok.slice(0, 1);
          if (files.length) onFiles(files);
        }}>
        <span className="filedrop-icon" aria-hidden="true">
          {status === "done" ? "✓" : status === "error" ? "!" : "⬆"}
        </span>
        <span>{label}</span>
        {required && !status && <span className="filedrop-tag">Required</span>}
        <input
          type="file"
          aria-label={label}
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            setRejected([]);
            if (files.length) onFiles(files);
          }}
        />
      </label>
      {rejected.length > 0 && (
        <p className="muted note note-warn">
          Not added ({accept}): {rejected.join(", ")}
        </p>
      )}
    </>
  );
}
